import { supabase } from '@/lib/supabase/client';
import type { GuildState } from '@/types/app-state';
import type { Database, Json } from '@/types/database';
import type {
  AppRole,
  AuditLog,
  AttendanceRecord,
  EventMass,
  EventParty,
  EventSlot,
  GuildBadgeDefinition,
  PartyTemplate,
  ProfileSignupStatistic,
  TemplateDraftRecord,
  PartyTemplateSlot,
  ProfileBadge,
  Profile,
  Signup,
  TemplatePartyLabel,
  WeaponCatalogItem,
} from '@/types/domain';

type TableName = keyof Database['public']['Tables'];
type Row<T extends TableName> = Database['public']['Tables'][T]['Row'];

export const emptyGuildState: GuildState = {
  profiles: [],
  badgeCatalog: [],
  profileSignupStats: [],
  weapons: [],
  templates: [],
  templateDrafts: [],
  templateSlots: [],
  events: [],
  eventParties: [],
  eventSlots: [],
  signups: [],
  attendanceRecords: [],
  auditLogs: [],
};

function assertSupabase() {
  if (!supabase) {
    throw new Error('Supabase is not configured.');
  }

  return supabase;
}

function unwrap<T>(label: string, error: { message: string } | null, data: T): T {
  if (error) {
    throw new Error(`${label}: ${error.message}`);
  }

  return data;
}

function asObjectRecord(value: Json | null): Record<string, unknown> | undefined {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return undefined;
}

function mapTemplatePartyLabels(value: Json): TemplatePartyLabel[] {
  if (!Array.isArray(value)) {
    return Array.from({ length: 4 }, (_, index) => ({
      partyNumber: index + 1,
      label: `Party ${index + 1}`,
      sortOrder: index + 1,
    }));
  }

  return Array.from({ length: 4 }, (_, index) => {
    const partyNumber = index + 1;
    const entry = value.find((item) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) {
        return false;
      }

      return Number((item as Record<string, Json>).party_number ?? (item as Record<string, Json>).partyNumber) === partyNumber;
    });

    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return {
        partyNumber,
        label: `Party ${partyNumber}`,
        sortOrder: partyNumber,
      };
    }

    const record = entry as Record<string, Json>;

    return {
      partyNumber,
      label:
        (typeof record.label === 'string' && record.label.trim()) ||
        `Party ${partyNumber}`,
      sortOrder:
        typeof record.sort_order === 'number'
          ? record.sort_order
          : typeof record.sortOrder === 'number'
            ? record.sortOrder
            : partyNumber,
    };
  });
}

function mapBadgeCatalog(rows: Row<'guild_badges'>[]): GuildBadgeDefinition[] {
  return rows.map((row) => ({
    id: row.id,
    slug: row.slug,
    displayName: row.display_name,
    description: row.description ?? undefined,
    accentColor: row.accent_color,
    isActive: row.is_active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}

function mapProfileBadges(
  rows: Row<'profile_badges'>[],
  badgeCatalogById: Map<string, GuildBadgeDefinition>,
): ProfileBadge[] {
  return rows.reduce<ProfileBadge[]>((badges, row) => {
      const badge = badgeCatalogById.get(row.badge_id);

      if (!badge) {
        return badges;
      }

      badges.push({
        profileId: row.profile_id,
        badgeId: row.badge_id,
        slug: badge.slug,
        displayName: badge.displayName,
        description: badge.description,
        accentColor: badge.accentColor,
        assignedBy: row.assigned_by ?? undefined,
        note: row.note ?? undefined,
        createdAt: row.created_at,
      });

      return badges;
    }, []);
}

function mapProfiles(
  profileRows: Row<'profiles'>[],
  roleRows: Row<'profile_roles'>[],
  profileBadges: ProfileBadge[],
): Profile[] {
  const rolesByProfileId = new Map<string, AppRole[]>();
  const badgesByProfileId = new Map<string, ProfileBadge[]>();

  for (const row of roleRows) {
    const existing = rolesByProfileId.get(row.profile_id) ?? [];
    existing.push(row.role);
    rolesByProfileId.set(row.profile_id, existing);
  }

  for (const badge of profileBadges) {
    const existing = badgesByProfileId.get(badge.profileId) ?? [];
    existing.push(badge);
    badgesByProfileId.set(badge.profileId, existing);
  }

  return profileRows.map((row) => ({
    id: row.id,
    discordId: row.discord_user_id ?? row.id,
    discordUsername: row.discord_username ?? 'discord-user',
    displayName: row.display_name,
    inGameName: row.in_game_name ?? undefined,
    avatarUrl: row.avatar_url ?? undefined,
    approvalStatus: row.approval_status,
    roles: rolesByProfileId.get(row.id) ?? ['member'],
    notes: row.notes ?? undefined,
    progressionLevelOverride: row.progression_level_override ?? undefined,
    chatMuted: row.chat_muted,
    chatMutedReason: row.chat_muted_reason ?? undefined,
    chatMutedAt: row.chat_muted_at ?? undefined,
    chatMutedBy: row.chat_muted_by ?? undefined,
    guildMembershipActive: row.guild_membership_active,
    dmMassAlertsEnabled: row.dm_mass_alerts_enabled,
    dmNotificationsPaused: row.dm_notifications_paused,
    dmLastSentAt: row.dm_last_sent_at ?? undefined,
    dmLastError: row.dm_last_error ?? undefined,
    badges: badgesByProfileId.get(row.id) ?? [],
    createdAt: row.created_at,
    approvedAt: row.approved_at ?? undefined,
  }));
}

function mapProfileSignupStats(
  rows: Database['public']['Views']['profile_signup_statistics']['Row'][],
): ProfileSignupStatistic[] {
  return rows.map((row) => ({
    profileId: row.profile_id,
    totalSignupRecords: row.total_signup_records,
    totalJoined: row.total_joined,
    totalWaitlist: row.total_waitlist,
    totalJoinedEvents: row.total_joined_events,
  }));
}

function mapWeapons(rows: Row<'weapon_catalog'>[]): WeaponCatalogItem[] {
  return rows.map((row) => ({
    id: row.id,
    slug: row.slug,
    filename: row.filename,
    displayName: row.display_name,
    roleCategory: row.role_category,
    imagePath: row.image_path,
    storageObjectPath: row.storage_object_path ?? undefined,
    isActive: row.is_active,
    notes: row.notes ?? undefined,
  }));
}

function mapTemplates(rows: Row<'party_templates'>[]): PartyTemplate[] {
  return rows.map((row) => ({
    id: row.id,
    name: row.name,
    description: row.description ?? '',
    isActive: row.is_active,
    partyLabels: mapTemplatePartyLabels(row.party_labels_json),
    createdBy: row.created_by ?? '',
    archivedAt: row.archived_at ?? undefined,
    archivedBy: row.archived_by ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}

function mapTemplateDrafts(rows: Row<'template_drafts'>[]): TemplateDraftRecord[] {
  return rows.map((row) => ({
    id: row.id,
    ownerProfileId: row.owner_profile_id,
    templateId: row.template_id ?? undefined,
    sourceTemplateId: row.source_template_id ?? undefined,
    sourceEventId: row.source_event_id ?? undefined,
    name: row.name,
    description: row.description ?? '',
    isActive: row.is_active,
    parties: Array.isArray(row.parties_json) ? (row.parties_json as unknown as TemplateDraftRecord['parties']) : [],
    templateUpdatedAtSnapshot: row.template_updated_at_snapshot ?? undefined,
    lastAutosavedAt: row.last_autosaved_at,
    lastRestoredAt: row.last_restored_at ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}

function mapTemplateSlots(rows: Row<'party_template_slots'>[]): PartyTemplateSlot[] {
  return rows.map((row) => ({
    id: row.id,
    templateId: row.template_id,
    partyNumber: row.party_number,
    slotOrder: row.slot_order,
    roleName: row.role_name,
    note: row.slot_note ?? undefined,
    roleCategory: row.role_category,
    weaponId: row.weapon_catalog_id ?? undefined,
    weaponName: row.weapon_name,
    imagePath: row.weapon_image_path,
    capacity: row.capacity,
    waitlistEnabled: row.waitlist_enabled,
    isActive: row.is_active,
  }));
}

function mapEvents(rows: Row<'events'>[]): EventMass[] {
  return rows.map((row) => ({
    id: row.id,
    title: row.title,
    description: row.description ?? '',
    templateId: row.template_id ?? undefined,
    createdBy: row.created_by,
    startsAt: row.starts_at,
    signupOpensAt: row.signup_opens_at,
    signupClosesAt: row.signup_closes_at,
    status: row.status,
    archivedAt: row.archived_at ?? undefined,
    archivedBy: row.archived_by ?? undefined,
    partyCount: row.party_count,
    waitlistEnabled: row.waitlist_enabled,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}

function mapEventParties(rows: Row<'event_parties'>[]): EventParty[] {
  return rows.map((row) => ({
    id: row.id,
    eventId: row.event_id,
    partyNumber: row.party_number,
    label: row.label,
    sortOrder: row.sort_order,
  }));
}

function mapEventSlots(rows: Row<'event_slots'>[]): EventSlot[] {
  return rows.map((row) => ({
    id: row.id,
    eventId: row.event_id,
    eventPartyId: row.event_party_id,
    templateSlotId: row.template_slot_id ?? undefined,
    partyNumber: row.party_number,
    slotOrder: row.slot_order,
    roleName: row.role_name,
    note: row.slot_note ?? undefined,
    roleCategory: row.role_category,
    weaponId: row.weapon_catalog_id ?? undefined,
    weaponName: row.weapon_name,
    imagePath: row.weapon_image_path,
    capacity: row.capacity,
    waitlistEnabled: row.waitlist_enabled,
    isActive: row.is_active,
  }));
}

function mapSignups(rows: Row<'signups'>[]): Signup[] {
  return rows.map((row) => ({
    id: row.id,
    eventId: row.event_id,
    eventSlotId: row.event_slot_id,
    profileId: row.profile_id,
    status: row.status,
    queuePosition: row.queue_position,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    joinedAt: row.joined_at ?? undefined,
    waitlistedAt: row.waitlisted_at ?? undefined,
    cancelledAt: row.cancelled_at ?? undefined,
    promotedAt: row.promoted_at ?? undefined,
    cancelledBy: row.cancelled_by ?? undefined,
    roleNameSnapshot: row.role_name_snapshot,
    slotNoteSnapshot: row.slot_note_snapshot ?? undefined,
    roleCategorySnapshot: row.role_category_snapshot,
    weaponNameSnapshot: row.weapon_name_snapshot,
    weaponImagePathSnapshot: row.weapon_image_path_snapshot,
    partyNumberSnapshot: row.party_number_snapshot,
  }));
}

function mapAuditLogs(rows: Row<'audit_logs'>[]): AuditLog[] {
  return rows.map((row) => ({
    id: row.id,
    actorProfileId: row.actor_profile_id ?? undefined,
    entityType: row.entity_type as AuditLog['entityType'],
    entityId: row.entity_id ?? undefined,
    action: row.action,
    metadata: asObjectRecord(row.metadata),
    createdAt: row.created_at,
  }));
}

function mapAttendanceRecords(rows: Row<'event_attendance_records'>[]): AttendanceRecord[] {
  return rows.map((row) => ({
    id: row.id,
    eventId: row.event_id,
    profileId: row.profile_id,
    sourceSignupId: row.source_signup_id,
    eventSlotId: row.event_slot_id,
    inGameNameSnapshot: row.in_game_name_snapshot,
    roleCategorySnapshot: row.role_category_snapshot,
    roleNameSnapshot: row.role_name_snapshot,
    weaponNameSnapshot: row.weapon_name_snapshot,
    weaponImagePathSnapshot: row.weapon_image_path_snapshot,
    partyNumberSnapshot: row.party_number_snapshot,
    attendanceLockedAt: row.attendance_locked_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}

export async function fetchGuildState(): Promise<GuildState> {
  const client = assertSupabase();

  const [
    profilesResponse,
    rolesResponse,
    badgeCatalogResponse,
    profileBadgesResponse,
    profileSignupStatsResponse,
    weaponsResponse,
    templatesResponse,
    templateDraftsResponse,
    templateSlotsResponse,
    eventsResponse,
    eventPartiesResponse,
    eventSlotsResponse,
    signupsResponse,
    attendanceResponse,
    auditLogsResponse,
  ] = await Promise.all([
    client
      .from('profiles')
      .select('*')
      .order('display_name', { ascending: true }),
    client.from('profile_roles').select('profile_id, role, granted_by, granted_at').order('granted_at', { ascending: true }),
    client
      .from('guild_badges')
      .select('id, slug, display_name, description, accent_color, is_active, created_at, updated_at')
      .order('display_name', { ascending: true }),
    client
      .from('profile_badges')
      .select('profile_id, badge_id, assigned_by, note, created_at')
      .order('created_at', { ascending: false }),
    client
      .from('profile_signup_statistics')
      .select('profile_id, total_signup_records, total_joined, total_waitlist, total_joined_events')
      .order('total_signup_records', { ascending: false }),
    client
      .from('weapon_catalog')
      .select('id, slug, filename, display_name, role_category, image_path, storage_object_path, is_active, notes, created_at, updated_at')
      .order('display_name', { ascending: true }),
    client
      .from('party_templates')
      .select('id, name, description, is_active, party_labels_json, created_by, archived_at, archived_by, created_at, updated_at')
      .order('created_at', { ascending: false }),
    client
      .from('template_drafts')
      .select(
        'id, owner_profile_id, template_id, source_template_id, source_event_id, name, description, is_active, parties_json, template_updated_at_snapshot, last_autosaved_at, last_restored_at, created_at, updated_at',
      )
      .order('updated_at', { ascending: false }),
    client
      .from('party_template_slots')
      .select(
        'id, template_id, party_number, slot_order, role_name, slot_note, role_category, weapon_catalog_id, weapon_name, weapon_image_path, capacity, waitlist_enabled, is_active, created_at, updated_at',
      )
      .order('template_id', { ascending: true })
      .order('party_number', { ascending: true })
      .order('slot_order', { ascending: true }),
    client
      .from('events')
      .select(
        'id, title, description, template_id, status, created_by, updated_by, starts_at, signup_opens_at, signup_closes_at, archived_at, archived_by, party_count, waitlist_enabled, created_at, updated_at',
      )
      .order('created_at', { ascending: false }),
    client
      .from('event_parties')
      .select('id, event_id, party_number, label, sort_order, created_at, updated_at')
      .order('event_id', { ascending: true })
      .order('sort_order', { ascending: true }),
    client
      .from('event_slots')
      .select(
        'id, event_id, event_party_id, template_slot_id, party_number, slot_order, role_name, slot_note, role_category, weapon_catalog_id, weapon_name, weapon_image_path, capacity, waitlist_enabled, is_active, created_at, updated_at',
      )
      .order('event_id', { ascending: true })
      .order('party_number', { ascending: true })
      .order('slot_order', { ascending: true }),
    client
      .from('signups')
      .select(
        'id, event_id, event_slot_id, profile_id, status, queue_position, created_at, updated_at, joined_at, waitlisted_at, cancelled_at, promoted_at, cancelled_by, role_name_snapshot, slot_note_snapshot, role_category_snapshot, weapon_name_snapshot, weapon_image_path_snapshot, party_number_snapshot',
      )
      .order('created_at', { ascending: false }),
    client
      .from('event_attendance_records')
      .select(
        'id, event_id, profile_id, source_signup_id, event_slot_id, in_game_name_snapshot, role_category_snapshot, role_name_snapshot, weapon_name_snapshot, weapon_image_path_snapshot, party_number_snapshot, attendance_locked_at, created_at, updated_at',
      )
      .order('attendance_locked_at', { ascending: false }),
    client
      .from('audit_logs')
      .select('id, actor_profile_id, entity_type, entity_id, action, before_state, after_state, metadata, created_at')
      .order('created_at', { ascending: false })
      .limit(25),
  ]);

  const profiles = unwrap('profiles', profilesResponse.error, profilesResponse.data ?? []);
  const roles = unwrap('profile_roles', rolesResponse.error, rolesResponse.data ?? []);
  const badgeCatalogRows = unwrap('guild_badges', badgeCatalogResponse.error, badgeCatalogResponse.data ?? []);
  const profileBadgeRows = unwrap('profile_badges', profileBadgesResponse.error, profileBadgesResponse.data ?? []);
  const profileSignupStats =
    profileSignupStatsResponse.error &&
    (/profile_signup_statistics/i.test(profileSignupStatsResponse.error.message) ||
      /permission denied|row-level security|policy/i.test(profileSignupStatsResponse.error.message))
      ? []
      : unwrap('profile_signup_statistics', profileSignupStatsResponse.error, profileSignupStatsResponse.data ?? []);
  const weapons = unwrap('weapon_catalog', weaponsResponse.error, weaponsResponse.data ?? []);
  const templates = unwrap('party_templates', templatesResponse.error, templatesResponse.data ?? []);
  const templateDrafts = unwrap('template_drafts', templateDraftsResponse.error, templateDraftsResponse.data ?? []);
  const templateSlots = unwrap('party_template_slots', templateSlotsResponse.error, templateSlotsResponse.data ?? []);
  const events = unwrap('events', eventsResponse.error, eventsResponse.data ?? []);
  const eventParties = unwrap('event_parties', eventPartiesResponse.error, eventPartiesResponse.data ?? []);
  const eventSlots = unwrap('event_slots', eventSlotsResponse.error, eventSlotsResponse.data ?? []);
  const signups = unwrap('signups', signupsResponse.error, signupsResponse.data ?? []);
  const attendanceRecords =
    attendanceResponse.error &&
    (/event_attendance_records/i.test(attendanceResponse.error.message) ||
      /permission denied|row-level security|policy/i.test(attendanceResponse.error.message))
      ? []
      : unwrap('event_attendance_records', attendanceResponse.error, attendanceResponse.data ?? []);
  const auditLogs = unwrap('audit_logs', auditLogsResponse.error, auditLogsResponse.data ?? []);

  const badgeCatalog = mapBadgeCatalog(badgeCatalogRows);
  const badgeCatalogById = new Map(badgeCatalog.map((badge) => [badge.id, badge]));
  const profileBadges = mapProfileBadges(profileBadgeRows, badgeCatalogById);

  return {
    profiles: mapProfiles(profiles, roles, profileBadges),
    badgeCatalog,
    profileSignupStats: mapProfileSignupStats(profileSignupStats),
    weapons: mapWeapons(weapons),
    templates: mapTemplates(templates),
    templateDrafts: mapTemplateDrafts(templateDrafts),
    templateSlots: mapTemplateSlots(templateSlots),
    events: mapEvents(events),
    eventParties: mapEventParties(eventParties),
    eventSlots: mapEventSlots(eventSlots),
    signups: mapSignups(signups),
    attendanceRecords: mapAttendanceRecords(attendanceRecords),
    auditLogs: mapAuditLogs(auditLogs),
  };
}
